import { useCallback, useState } from 'react';
import type { ExtrasEditResult } from '../ExtrasEditModal';
import type { GuestOption } from '../GuestSelectModal/GuestSelectModal.types';
import type { ExtrasItem, ExtrasSelection, UseExtrasSelectionOptions } from './ExtrasModals.types';

const OPTION_TYPES = [
  'per_date',
  'per_date_range',
  'per_time_range',
  'per_date_time',
  'per_date_time_range',
] as const;

export const isOptionType = (item: ExtrasItem) =>
  !!item.type && (OPTION_TYPES as readonly string[]).includes(item.type);

export const usesQuantityModal = (item: ExtrasItem) =>
  item.type === 'per_stay' && item.perPerson === false && (item.maxQuantity ?? 1) > 1;

export function useExtrasSelection({
  items,
  guests,
  onAdd,
  onConfirmGuestSelection,
  onConfirmSelection,
}: UseExtrasSelectionOptions): ExtrasSelection {
  const isMultiGuest = (guests?.length ?? 0) > 1;

  const usesGuestModal = useCallback(
    (item: ExtrasItem) =>
      isMultiGuest && (item.perPerson ?? true) && (!item.type || item.type === 'per_stay'),
    [isMultiGuest]
  );

  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [modalGuests, setModalGuests] = useState<GuestOption[]>([]);
  const [editModalItemId, setEditModalItemId] = useState<string | null>(null);

  const openModal = useCallback(
    (itemId: string, preselectedIds?: string[]) => {
      if (!guests) return;
      const item = items.find((i) => i.id === itemId);
      if (!item) return;

      const guestOptions: GuestOption[] = guests.map((guest) => ({
        id: guest.id,
        label: guest.label,
        selected: preselectedIds ? preselectedIds.includes(guest.id) : true,
        price: { amount: item.price.amount, currencyCode: item.price.currencyCode },
      }));

      setModalGuests(guestOptions);
      setModalItemId(itemId);
    },
    [guests, items]
  );

  const handleToggleModalGuest = useCallback((guestId: string) => {
    setModalGuests((prev) =>
      prev.map((g) => (g.id === guestId ? { ...g, selected: !g.selected } : g))
    );
  }, []);

  const handleConfirmModal = useCallback(() => {
    if (!modalItemId || !onConfirmGuestSelection) return;
    const selectedIds = modalGuests.filter((g) => g.selected).map((g) => g.id);
    onConfirmGuestSelection(modalItemId, selectedIds);
    setModalItemId(null);
    setModalGuests([]);
  }, [modalItemId, modalGuests, onConfirmGuestSelection]);

  const handleCloseModal = useCallback(() => {
    setModalItemId(null);
    setModalGuests([]);
  }, []);

  const handleConfirmEditModal = useCallback(
    (result: ExtrasEditResult) => {
      if (!editModalItemId) return;
      onConfirmSelection?.(editModalItemId, result);
      setEditModalItemId(null);
    },
    [editModalItemId, onConfirmSelection]
  );

  const handleCloseEditModal = useCallback(() => setEditModalItemId(null), []);

  const handleAdd = useCallback(
    (item: ExtrasItem) => {
      if (isOptionType(item) || usesQuantityModal(item)) {
        setEditModalItemId(item.id);
      } else if (usesGuestModal(item)) {
        openModal(item.id);
      } else {
        onAdd(item.id);
      }
    },
    [usesGuestModal, openModal, onAdd]
  );

  const handleEditSelections = useCallback(
    (item: ExtrasItem) => {
      if (isOptionType(item) || usesQuantityModal(item)) {
        setEditModalItemId(item.id);
      } else if (usesGuestModal(item) && item.selectedGuestIds) {
        openModal(item.id, item.selectedGuestIds);
      }
    },
    [usesGuestModal, openModal]
  );

  const canEditSelections = useCallback(
    (item: ExtrasItem) => isOptionType(item) || usesQuantityModal(item) || usesGuestModal(item),
    [usesGuestModal]
  );

  return {
    isMultiGuest,
    modalItemId,
    modalGuests,
    editModalItemId,
    handleAdd,
    handleEditSelections,
    canEditSelections,
    handleToggleModalGuest,
    handleConfirmModal,
    handleCloseModal,
    handleConfirmEditModal,
    handleCloseEditModal,
  };
}
